export type Unsubscribe = () => void

export interface NavigationEvents {
	onBeforeHistoryChange: (callback: () => void) => Unsubscribe
}

/**
 * Adapter for Next.js App Router navigation events.
 * Tracks SPA history mutations (push/replace) and browser navigation (back/forward).
 */
const BEFORE_HISTORY_CHANGE_EVENT = 'app:nav:before-history-change'
const HISTORY_PATCH_STORE_KEY = '__appNavHistoryPatchStore'

type HistoryPatchStore = {
	refCount: number
	originalPushState: History['pushState']
	originalReplaceState: History['replaceState']
}

const emitBeforeHistoryChange = () => {
	window.dispatchEvent(new Event(BEFORE_HISTORY_CHANGE_EVENT))
}

const getHistoryPatchStore = (): HistoryPatchStore | undefined => {
	return (window as typeof window & { [HISTORY_PATCH_STORE_KEY]?: HistoryPatchStore })[HISTORY_PATCH_STORE_KEY]
}

const setHistoryPatchStore = (store: HistoryPatchStore) => {
	;(window as typeof window & { [HISTORY_PATCH_STORE_KEY]?: HistoryPatchStore })[HISTORY_PATCH_STORE_KEY] = store
}

const removeHistoryPatchStore = () => {
	delete (window as typeof window & { [HISTORY_PATCH_STORE_KEY]?: HistoryPatchStore })[HISTORY_PATCH_STORE_KEY]
}

const installHistoryPatch = () => {
	const existingStore = getHistoryPatchStore()

	if (existingStore) {
		existingStore.refCount += 1
		return
	}

	const originalPushState = window.history.pushState.bind(window.history)
	const originalReplaceState = window.history.replaceState.bind(window.history)

	window.history.pushState = (...args) => {
		emitBeforeHistoryChange()
		return originalPushState(...args)
	}

	window.history.replaceState = (...args) => {
		emitBeforeHistoryChange()
		return originalReplaceState(...args)
	}

	setHistoryPatchStore({
		refCount: 1,
		originalPushState,
		originalReplaceState
	})
}

const uninstallHistoryPatch = () => {
	const store = getHistoryPatchStore()

	if (!store) {
		return
	}

	store.refCount -= 1

	if (store.refCount > 0) {
		return
	}

	window.history.pushState = store.originalPushState
	window.history.replaceState = store.originalReplaceState
	removeHistoryPatchStore()
}

export function createNextRouterNavigation(): NavigationEvents {
	return {
		onBeforeHistoryChange(callback: () => void): Unsubscribe {
			if (typeof window === 'undefined') return () => {}

			installHistoryPatch()

			const handler = () => callback()

			window.addEventListener(BEFORE_HISTORY_CHANGE_EVENT, handler)
			window.addEventListener('popstate', handler)
			window.addEventListener('hashchange', handler)

			return () => {
				window.removeEventListener(BEFORE_HISTORY_CHANGE_EVENT, handler)
				window.removeEventListener('popstate', handler)
				window.removeEventListener('hashchange', handler)
				uninstallHistoryPatch()
			}
		}
	}
}
